import { useTheme } from "@carrotfertility/carotene-core"
import React from "react"
import { Skeleton, type SkeletonProps } from "./Skeleton"

/**
 * Carotene's medium `Button` keeps its block padding in a component styleOverride rather than on the
 * theme, so this one term of the sum cannot be read back off the theme the way the rest are.
 *
 * ⚠ RE-CHECK THIS ON A CAROTENE UPGRADE. It is a copy of `sizeStyles.medium`, not a reference to it:
 * carotene can change its padding without breaking a build here, leaving a frame that is quietly the
 * wrong height. Verified against carotene-core 4.1.0.
 */
const BUTTON_BLOCK_PADDING = "0.6875rem"

/**
 * The line box, the block padding and both borders — the three things a `Button` is tall by.
 *
 * ⚠ RE-CHECK THIS ON A CAROTENE UPGRADE TOO: `/ 100` reads `lineHeight` as the percentage carotene
 * writes today. A unitless ratio or a `px` value still parses, so a switch would silently collapse
 * the frame rather than fail to compile. Verified against carotene-core 4.1.0.
 */
const blockSize = (fontSize: string, lineHeight: string, borderWidth: string): string =>
  `calc(${fontSize} * ${parseFloat(lineHeight) / 100} + 2 * (${BUTTON_BLOCK_PADDING} + ${borderWidth}))`

export type ButtonSkeletonProps = Pick<SkeletonProps, "width" | "sx">

/**
 * A placeholder the size of carotene's medium `Button`, for a frame standing in for one.
 *
 * ⚠ THE CONTROL IS NOT ONE HEIGHT, which is why this is derived rather than written down. Below
 * carotene's `sm` the label's line box grows from 125% to 150% of 1rem and the control grows with
 * it, so a frame pinned to the desktop 46px is 4px short on a phone — and a frame that does not
 * match the control it replaces moves the page when the read lands, which is the one thing a
 * skeleton exists to prevent.
 */
export function ButtonSkeleton({ width, sx }: ButtonSkeletonProps): JSX.Element {
  const theme = useTheme()
  const mobile = theme.breakpoints.down("sm")
  const { fontSize, lineHeight } = theme.typography.button
  // Carotene declares the mobile line box as a nested media key on the `button` role, which MUI's
  // `TypographyStyle` does not describe.
  const mobileLineHeight =
    (theme.typography.button as unknown as Record<string, { lineHeight?: string } | undefined>)[mobile]?.lineHeight ??
    String(lineHeight)
  const border = theme.tokens.borderWidth.md

  return (
    <Skeleton
      width={width}
      sx={[
        {
          blockSize: blockSize(String(fontSize), String(lineHeight), border),
          [mobile]: { blockSize: blockSize(String(fontSize), mobileLineHeight, border) },
          borderRadius: theme.tokens.borderRadius.md
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
      ]}
    />
  )
}
